import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button premium (F3) — patrón base machette-tech ampliado.
 *
 * - rounded-xl (12 px) + font-semibold.
 * - hover usa brightness en lugar de tintar el bg (más limpio sobre brand).
 * - focus-visible ring derivado de los tokens del :root.
 * - Tamaños: sm (h-9), default (h-10), md (h-11 premium), lg (h-12), icon (h-9 w-9), icon-sm (h-8 w-8).
 * - Variantes nuevas: success, warning (alineadas con tokens semánticos del index.css F1).
 *
 * Compatibilidad hacia atrás: las variantes "default/destructive/outline/
 * secondary/ghost/link" mantienen sus nombres. Solo cambia su look.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold',
    'transition-[background-color,box-shadow,filter,transform] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:brightness-110 active:brightness-95 shadow-sm hover:shadow',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-110 active:brightness-95 shadow-sm',
        success: 'bg-success text-success-foreground hover:brightness-110 active:brightness-95 shadow-sm',
        warning: 'bg-warning text-warning-foreground hover:brightness-105 active:brightness-95 shadow-sm',
        outline: 'border border-input bg-background hover:bg-accent/10 hover:border-foreground/20',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        ghost: 'hover:bg-accent/10 hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-9 px-3 text-xs',
        md: 'h-11 px-5',
        lg: 'h-12 px-6 text-base',
        icon: 'h-9 w-9 p-0',
        'icon-sm': 'h-8 w-8 p-0 rounded-lg',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)
Button.displayName = 'Button'

export { buttonVariants }
